<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updatePlan">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update Plan</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['portal_id']" :items="portals" :loading="!portals.length"
                                      :rules="[vRules.required]" density="compact" item-title="name" item-value="id"
                                      label="Portal" @update:model-value="()=>{
                                          formData['skus']=null;fetchProductSkusList()
                                      }"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['skus']" :items="productSkus" :loading="!productSkus.length"
                                      :rules="[vRules.required]" chips density="compact" item-title="sku"
                                      item-value="id" label="Product Skus" multiple/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['price']" :rules="[vRules.required]" density="compact"
                                          label="Price" type="number"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['interval']" :items="intervalTypes" :rules="[vRules.required]"
                                      density="compact" label="Interval"/>
                        </v-col>

                        <!-- Go (personal portal) plans: premium flag + entitlement
                             feature values. Hidden for fleet plans. -->
                        <template v-if="portalType === 'personal'">
                            <v-col cols="12"><v-divider/></v-col>
                            <v-col cols="12" sm="4">
                                <v-switch v-model="formData['is_premium']" color="primary" density="compact"
                                          hide-details label="Premium plan"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model.number="formData['tier_rank']" density="compact"
                                              label="Tier rank" type="number"/>
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field v-model.number="formData['max_members']" density="compact"
                                              hint="Blank = not applicable" label="Max members" persistent-hint
                                              type="number"/>
                            </v-col>
                            <v-col cols="12"><div class="text-subtitle-2">Premium features</div></v-col>
                            <v-col v-for="f in featureCatalog" :key="f.key" cols="12" sm="6">
                                <v-switch v-if="f.type === 'bool'" v-model="formData.features[f.key]"
                                          color="primary" density="compact" hide-details
                                          :label="f.label || f.key"/>
                                <v-text-field v-else v-model.number="formData.features[f.key]" density="compact"
                                              :hint="f.type === 'unlimited' ? '-1 = unlimited' : ''" persistent-hint
                                              :label="f.label || f.key" type="number"/>
                            </v-col>
                        </template>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, computed, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    plan: {
        type: Object
    }
});

const formData = reactive({
    valid: false,
    skus: null,
    name: props.plan.name ?? null,
    price: props.plan.price ?? null,
    interval: props.plan.interval ?? null,
    portal_id: props.plan.portal_id ?? null,
    is_premium: !!props.plan.is_premium,
    tier_rank: props.plan.tier_rank ?? 0,
    max_members: props.plan.max_members ?? null,
    features: {},
});
const emits = defineEmits(['update:modelValue', 'updatePlan']);
const dialogSwitch = ref(props.modelValue);
const productSkus = ref([]);
const portals = ref([]);
const featureCatalog = ref([]);
const errors = ref([]);
const intervalTypes = [
    {value: 0, title: 'Monthly'},
    {value: 1, title: 'Quarterly'},
    {value: 2, title: 'Yearly'},
];
const portalType = computed(() => {
    // Null-safe: portals load async while portal_id is already set from the
    // plan, so find() can be undefined on the first renders.
    const p = portals.value.find((item) => item.id === formData['portal_id']);
    return p ? p.type : null;
})

onMounted(async () => {
    if (props.plan && props.plan.product_skus.length) {
        formData['skus'] = props.plan.product_skus.map((sku) => sku.id)
    }
    await fetchPortals().then((response) => {
        fetchProductSkusList();
    })
    await fetchFeatureCatalog();
    initFeatures();
});

// Re-init when the portal changes mid-edit (personal -> prefill, else clear).
watch(portalType, () => initFeatures());

const fetchFeatureCatalog = () => {
    return send('get', `admin/feature-catalog`)
        .then((response) => {
            featureCatalog.value = response.data.data;
        })
        .catch(() => {});
};

// Prefill each feature from the plan's existing override if present, else the
// catalog default, decoded to the field's native type. Only personal-portal
// plans carry features; fleet plans send none (no overrides created for them).
const initFeatures = () => {
    if (portalType.value !== 'personal') {
        formData.features = {};
        return;
    }
    const overrides = {};
    (props.plan.plan_features ?? []).forEach((pf) => {
        overrides[pf.feature_key] = pf.value;
    });
    const f = {};
    featureCatalog.value.forEach((c) => {
        const raw = Object.prototype.hasOwnProperty.call(overrides, c.key)
            ? overrides[c.key] : c.default_value;
        if (c.type === 'bool') {
            f[c.key] = ['1', 'true', 'yes'].includes(String(raw).toLowerCase());
        } else {
            f[c.key] = raw === null || raw === undefined ? null : Number(raw);
        }
    });
    formData.features = f;
};

const fetchPortals = () => {
    return send('get', `admin/portals`)
        .then((response) => {
            portals.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Portals could not be loaded', 'error')
        })
}
const fetchProductSkusList = () => {
    return send('get', `product-skus`, {list: true, type: portalType.value})
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
            snackbar.showSnackbar('Product skus list could not be loaded', 'error')
        })
}
const updatePlan = () => {
    errors.value = [];
    send('put', `admin/subscription-plans/${props.plan.id}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updatePlan', response.data.data);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
